import { AppRoutes } from 'constants/AppRoutes';

import { isElevatedSeverity } from './groupWatchRuns';
import { findingPayloadString } from './topicWatchAgents';
import { isInvestigationConclusion } from './watchInvestigation';
import { formatAgentType } from './watchTowerSelectors';

import type { WatchFinding, WatchRun, WatchTowerMockState } from './types';

export const MARKETING_OPS_MOCK_TODAY = '2026-09-02T12:00:00.000Z';

export type PulseWindowDays = 7 | 30;

export type RiskGroupBy = 'company' | 'industry' | 'country' | 'agent';

export interface IncidentCard {
  finding: WatchFinding,
  companyName: string,
  country: string,
  industry: string,
  agentType: string,
  agentLabel: string,
  impactLine: string,
  affectedCount: number,
  run: WatchRun | null,
}

export interface PulseKpis {
  elevatedCount: number,
  companiesWithAlerts: number,
  newOpportunities: number,
  agentMix: { findingType: string, count: number }[],
}

export interface MarketingOpsFeedFilters {
  severity: string,
  agentType: string,
  country: string,
  industry: string,
  hasAffected: '' | 'yes' | 'no',
}

export interface RiskGroup {
  key: string,
  label: string,
  count: number,
  cards: IncidentCard[],
}

const SEVERITY_RANK: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export function mockPulseNow(): Date {
  return new Date(MARKETING_OPS_MOCK_TODAY);
}

export function observedInWindow(observedAt: string, days: number, now: Date): boolean {
  const stamp = new Date(observedAt).getTime();
  const end = now.getTime();
  const start = end - days * 24 * 60 * 60 * 1000;
  return stamp >= start && stamp <= end;
}

export function severityRank(severity: string): number {
  return SEVERITY_RANK[severity] ?? 9;
}

function companyForFinding(state: WatchTowerMockState, companyId: number | null) {
  if (!companyId) {
    return null;
  }
  return state.watchCompanies.find((item) => item.id === companyId) || null;
}

function runForFinding(state: WatchTowerMockState, runId: number): WatchRun | null {
  return state.runs.find((item) => item.id === runId) || null;
}

function affectedCount(state: WatchTowerMockState, findingId: number): number {
  return state.findingAffectedCompanies.filter((link) => link.findingId === findingId).length;
}

function impactLine(state: WatchTowerMockState, finding: WatchFinding): string {
  const talk = state.findingAffectedCompanies.find((link) => (
    link.findingId === finding.id && link.talkingPoint
  ));
  if (talk?.talkingPoint) {
    return talk.talkingPoint;
  }
  const text = finding.description || '';
  const sentence = text.split('.')[0];
  return sentence || finding.title;
}

export function toIncidentCard(state: WatchTowerMockState, finding: WatchFinding): IncidentCard {
  const company = companyForFinding(state, finding.watchCompanyId);
  const run = runForFinding(state, finding.agentRunId);
  const agentType = run?.type || '';
  const topic = finding.watchTopicId
    ? (state.watchTopics || []).find((item) => item.id === finding.watchTopicId)
    : null;
  return {
    finding,
    companyName: company?.name || topic?.title || String(finding.watchCompanyId || '—'),
    country: company?.addressCountry || findingPayloadString(finding, 'country'),
    industry: company?.naceSection || '',
    agentType,
    agentLabel: agentType ? formatAgentType(agentType) : '—',
    impactLine: impactLine(state, finding),
    affectedCount: affectedCount(state, finding.id),
    run,
  };
}

export function elevatedCardsInWindow(
  state: WatchTowerMockState,
  days: PulseWindowDays,
  now = mockPulseNow(),
): IncidentCard[] {
  return state.findings
    .filter((finding) => !isInvestigationConclusion(finding))
    .filter((finding) => (
      finding.elevated === true
      || (finding.elevated !== false && isElevatedSeverity(finding.severity))
    ))
    .filter((finding) => observedInWindow(finding.observedAt, days, now))
    .map((finding) => toIncidentCard(state, finding))
    .sort((left, right) => {
      const byDate = right.finding.observedAt.localeCompare(left.finding.observedAt);
      if (byDate) {
        return byDate;
      }
      return severityRank(left.finding.severity) - severityRank(right.finding.severity);
    });
}

export function pulseKpis(cards: IncidentCard[]): PulseKpis {
  const companies = new Set(
    cards.map((card) => card.finding.watchCompanyId).filter((id): id is number => Boolean(id)),
  );
  const mix = new Map<string, number>();
  cards.forEach((card) => {
    const key = card.finding.findingType;
    mix.set(key, (mix.get(key) || 0) + 1);
  });
  return {
    elevatedCount: cards.length,
    companiesWithAlerts: companies.size,
    newOpportunities: cards.filter((card) => card.affectedCount > 0).length,
    agentMix: Array.from(mix.entries())
      .map(([findingType, count]) => ({ findingType, count }))
      .sort((left, right) => left.findingType.localeCompare(right.findingType)),
  };
}

export function filterIncidentCardsByFoundDays(
  cards: IncidentCard[],
  startDay: string,
  endDay: string,
): IncidentCard[] {
  return cards.filter((card) => {
    const day = card.finding.observedAt.slice(0, 10);
    return day >= startDay && day <= endDay;
  });
}

export function filterIncidentCards(
  cards: IncidentCard[],
  filters: MarketingOpsFeedFilters,
): IncidentCard[] {
  return cards.filter((card) => {
    if (filters.severity && card.finding.severity !== filters.severity) {
      return false;
    }
    if (filters.agentType && card.agentType !== filters.agentType) {
      return false;
    }
    if (filters.country && card.country !== filters.country) {
      return false;
    }
    if (filters.industry && card.industry !== filters.industry) {
      return false;
    }
    if (filters.hasAffected === 'yes' && card.affectedCount === 0) {
      return false;
    }
    if (filters.hasAffected === 'no' && card.affectedCount > 0) {
      return false;
    }
    return true;
  });
}

export function groupIncidentCards(cards: IncidentCard[], groupBy: RiskGroupBy): RiskGroup[] {
  const buckets = new Map<string, IncidentCard[]>();
  cards.forEach((card) => {
    let key = '';
    if (groupBy === 'company') {
      key = card.companyName;
    } else if (groupBy === 'industry') {
      key = card.industry || '—';
    } else if (groupBy === 'country') {
      key = card.country || '—';
    } else {
      key = card.agentLabel;
    }
    buckets.set(key, [...(buckets.get(key) || []), card]);
  });
  return Array.from(buckets.entries())
    .map(([key, groupCards]) => ({
      key,
      label: key,
      count: groupCards.length,
      cards: groupCards,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function watchTowerRunHref(run: WatchRun, publicId: string): string {
  const search = new URLSearchParams({
    scheduleId: String(run.scheduleId),
    scheduledAt: run.scheduledAt,
    runId: String(run.id),
    finding: publicId,
  });
  if (run.watchTopicId) {
    search.set('topicId', String(run.watchTopicId));
  }
  return `${AppRoutes.watchTowerRuns}?${search.toString()}`;
}

export function feedFilterOptions(cards: IncidentCard[]): {
  countries: string[],
  industries: string[],
  agentTypes: string[],
} {
  return {
    countries: Array.from(new Set(cards.map((card) => card.country).filter(Boolean))).sort(),
    industries: Array.from(new Set(cards.map((card) => card.industry).filter(Boolean))).sort(),
    agentTypes: Array.from(new Set(cards.map((card) => card.agentType).filter(Boolean))).sort(),
  };
}
